import types from './types';
import { IReturnError } from './reducers';
import returnsService from 'src/services/returns/returnsService';
import {
  IEligibleFilter,
  IEligibleResponse,
  IStatusFilter,
  IStatusResponse,
  IEligibleItem,
  IReturnAlert,
  IReturnOptionConfig,
  IReturnSubmittedOption,
  IReturnEditedOption,
  IReturnGenerateShippingLabel,
  IReturnShippingConfig
} from 'src/services/returns/typings';
import {
  ADD_EXISTING,
  SAVE_FOR_LATER
} from 'src/pages/ReturnsView/components/ReturnsCartModal/components/ReturnsOptions/ReturnsOptions';
import { downloadFile } from 'src/utils/downloadHelper';
import localeFormatingInstance from 'src/translation/localeFormating';
import { analyticsActions } from 'src/analytics/store';

const getEligibleItems = (body?: IEligibleFilter, queryParams?: string) => (
  dispatch: any
) => {
  dispatch(setReturnError());
  dispatch(setReturnIsPending(true));

  returnsService
    .getEligibleItems(body, queryParams)
    .then(res => {
      const eligibleData = res.data.map(r => {
        return { ...r, coreItem: false, eligibleReturnId: r.id };
      });
      dispatch(setEligibleData({ ...res, data: eligibleData }));
      dispatch(setReturnIsPending(false));
    })
    .catch(err => {
      let errMessage = 'no data';
      if (err && err.response && err.response.data) {
        errMessage = err.response.data.message;
      }
      dispatch(
        setReturnError({
          hasError: true,
          message: errMessage,
          status: err.response.status
        })
      );
      dispatch(
        setEligibleData({
          data: [],
          pageNumber: 0,
          pageSize: 20,
          totalElements: 0,
          totalPages: 0
        })
      );
      dispatch(setReturnIsPending(false));
    });
};

const getEligibleCoreItems = (body?: IEligibleFilter, queryParams?: string) => (
  dispatch: any
) => {
  dispatch(setReturnError());
  dispatch(setReturnIsPending(true));

  returnsService
    .getEligibleCoreItems(body, queryParams)
    .then(res => {
      const eligibleCoreData = res.data.map(r => {
        return { ...r, coreItem: true, eligibleReturnId: r.id };
      });
      dispatch(setEligibleCoreData({ ...res, data: eligibleCoreData }));
      dispatch(setReturnIsPending(false));
    })
    .catch(err => {
      let errMessage = 'no data';
      if (err && err.response && err.response.data) {
        errMessage = err.response.data.message;
      }
      dispatch(
        setReturnError({
          hasError: true,
          message: errMessage,
          status: err.response.status
        })
      );
      dispatch(
        setEligibleCoreData({
          data: [],
          pageNumber: 0,
          pageSize: 20,
          totalElements: 0,
          totalPages: 0
        })
      );
      dispatch(setReturnIsPending(false));
    });
};

const getStatusItems = (body?: IStatusFilter, queryParams?: string) => (
  dispatch: any
) => {
  dispatch(setReturnError());
  dispatch(setReturnIsPending(true));

  returnsService
    .getStatusItems(body, queryParams)
    .then(res => {
      dispatch(setStatusData(res));
      dispatch(setReturnIsPending(false));
    })
    .catch(error => {
      dispatch(
        setReturnError({
          hasError: true,
          message:
            'You have no RMAs to view. Once you place new returns requests, they will be shown below.'
        })
      );
      dispatch(setReturnIsPending(false));
    });
};

const getRMALists = (body?: IStatusFilter, queryParams?: string) => (
  dispatch: any
) => {
  dispatch(setReturnError());
  dispatch(setReturnIsPending(true));

  returnsService
    .getStatusItems(body, 'size=1000')
    .then(res => {
      const rmaList = res.data.map(r => r.rmaNumber);

      dispatch(setRmaList(rmaList));
      dispatch(setReturnIsPending(false));
    })
    .catch(error => {
      dispatch(
        // setReturnError({
        //   hasError: true,
        //   message:
        //     'An error has occurred when submitting return. Please contact support team via email at FeedbackCA@reliableparts.com and include your Account Number, Invoice Number/RMA Number and a screen capture.'
        // })
        setReturnAlert({
          show: true,
          type: 'error',
          message:
            'You have no parts that are eligible for return. Once you place new orders, they will be shown below',
          icon: 'rpds-error'
        })
      );
      dispatch(setReturnIsPending(false));
    });
};

const addPartToReturnList = (part: IEligibleItem) => (
  dispatch: any,
  getState: any
) => {
  dispatch(setPartsToReturn([...getState().returns.partsToReturn, part]));
};

const removePartFromReturnList = (id: number) => (
  dispatch: any,
  getState: any
) => {
  const partsToReturn = getState().returns.partsToReturn;
  const parts = partsToReturn.filter((p: IEligibleItem) => p.id !== id);

  dispatch(setPartsToReturn(parts));
};

const editPartFromReturnList = (part: IEligibleItem) => (
  dispatch: any,
  getState: any
) => {
  const partsToReturn = getState().returns.partsToReturn;
  const parts = partsToReturn.map((p: IEligibleItem) => {
    if (p.id === part.id) {
      return part;
    } else {
      return p;
    }
  });

  dispatch(setPartsToReturn(parts));
};

const addExistingRMAList = (
  config: IReturnOptionConfig,
  callback: () => void,
  dispatch: any
) => {
  returnsService
    .addItemsToRmaList(config.rmaNumber, config.items)
    .then(() => {
      dispatch(setReturnIsPending(false));
      dispatch(
        setReturnAlert({
          show: true,
          type: 'success',
          message: `Return No. ${config.rmaNumber} has been saved.`,
          icon: 'rpds-check-circle'
        })
      );
      dispatch(setPartsToReturn([]));
      dispatch(getEligibleItems({}));
      dispatch(getEligibleCoreItems({}));
      if (config.submissionType === SAVE_FOR_LATER) {
        config.items.forEach(item => {
          dispatch(
            analyticsActions.rmaReturns(
              item,
              'saveReturnsForLater',
              config.rmaNumber,
              config.type
            )
          );
        });
      }
      callback();
    })
    .catch(() => {
      dispatch(setReturnIsPending(false));
      dispatch(
        setReturnError({
          hasError: true,
          message:
            'Please review your RMA as one or more of your items is no longer eligible for return. Please remove these items and save/submit again.'
        })
      );
      dispatch(
        setReturnAlert({
          show: true,
          type: 'error',
          message: 'An error has ocurred while data was being saved',
          icon: 'rpds-error'
        })
      );
    });
};

const addNewRMAList = async (
  config: IReturnOptionConfig,
  callback: () => void,
  dispatch: any
) => {
  let rmaNumber = '';
  try {
    rmaNumber = await returnsService.createNewRmaList(config.poNumber);
    config.rmaNumber = rmaNumber;
    addExistingRMAList(config, callback, dispatch);
  } catch (error) {
    dispatch(setReturnIsPending(false));
    dispatch(
      setReturnAlert({
        show: true,
        type: 'error',
        message:
          'Please review your RMA as one or more of your items is no longer eligible for return. Please remove these items and save/submit again.',
        icon: 'rpds-error'
      })
    );
  }
};


const addNewRMAListForShiipingLabel = async (
  config: IReturnOptionConfig,
  callback: () => void,
  dispatch: any
) => {
  let rmaNumber = '';
  try {
    rmaNumber = await returnsService.createNewRmaList(config.poNumber);
    config.rmaNumber = rmaNumber;
    callback();
    // addExistingRMAList(config, callback, dispatch);
  } catch (error) {
    dispatch(setReturnIsPending(false));
    dispatch(
      setReturnAlert({
        show: true,
        type: 'error',
        message:
          'Please review your RMA as one or more of your items is no longer eligible for return. Please remove these items and save/submit again.',
        icon: 'rpds-error'
      })
    );
  }
};

const submitRMAList = (
  config:IReturnShippingConfig,
  rmaNumber: string,
  callback: () => void,
  dispatch: any
) => {
  returnsService
    .submitRMAList(config,rmaNumber)
    .then(() => {
      dispatch(setReturnIsPending(false));
      dispatch(getEligibleItems({}));
      dispatch(getEligibleCoreItems({}));
      callback();
      dispatch(
        setReturnError({
          hasError: false,
          message: 'Returns submitted Sucessfully.',
          status: 200
        })
      );
    })
    .catch(() => {
      dispatch(setReturnIsPending(false));
      // callback();
      dispatch(
        setReturnError({
          hasError: true,
          message:
            'An error has occurred when submitting return. Please contact support team via email at FeedbackCA@reliableparts.com and include your Account Number, Invoice Number/RMA Number and a screen capture.',
          status: 500
        })
      );
    });
};


const submitRMAListWithShippingDetails = (
  config:IReturnShippingConfig,
  rmaNumber: string,
  callback: () => void,
  dispatch: any
) => {
  returnsService
    .submitRMAList(config,rmaNumber)
    .then(() => {
      dispatch(setReturnIsPending(false));
      dispatch(getEligibleItems({}));
      dispatch(getEligibleCoreItems({}));
      callback();
      dispatch(
        setReturnError({
          hasError: false,
          message: 'Returns submitted Sucessfully.',
          status: 200
        })
      );
    })
    .catch(() => {
      dispatch(setReturnIsPending(false));
      // callback();
      dispatch(
        setReturnError({
          hasError: true,
          message:
            'An error has occurred when submitting return. Please contact support team via email at FeedbackCA@reliableparts.com and include your Account Number, Invoice Number/RMA Number and a screen capture.',
          status: 500
        })
      );
    });
};

const submitRMAListWithShippingLabelRequest = (
  config:IReturnShippingConfig,
  rmaNumber: string,
  frm: string,
  callback: (resUpsCharges?: any[]) => void,
  dispatch: any
) => {
  // dispatch(setReturnIsPending(true));
  // dispatch(setReturnError());
  const newUpsBoxWithRmaNumber = config.shippingLabelRequest.map((item: any, index: any) => {
    config.shippingLabelRequest[index].rmaNumber=rmaNumber;
    return item;
  });
  returnsService.generateShippingLabel(newUpsBoxWithRmaNumber)
    .then((response: any) => {
    /* Send Generated Warehouse code and tracking number to SUbmit */
    const upsTrakingNoArr: any=[];
    const warehouseCodeArr: any=[];
    let selectedWarehouseCode='';
    if(response.message!==null){
      dispatch(setCloseModalIfError(true));
      /* If Shipping Lable thorws any error do not submit RMA and show Error message and delete created RMA number */
      if(frm==="FROMNEW"){
        returnsService
        .deleteRMAList(rmaNumber)
        .then(() => {
          dispatch(setReturnIsPending(false));
        })
        .catch(() => {
          dispatch(setReturnIsPending(false));
          dispatch(
            setReturnStatusAlert({
              type: 'error',
              message:
                'An error has occurred when deleting created return while generating Shipping Label.',
              show: true,
              icon: 'rpds-error'
            })
          );
          callback();
        });

      }
      dispatch(setReturnIsPending(false));
      // callback();
      dispatch(
        setReturnAlert({
          show: true,
          type: 'error',
          message: 'We are unable to create return. Please check your address for any incorrect / incomplete information and try again, otherwise contact our support team',
          icon: 'rpds-error'
        })
      );
      dispatch(setCloseModalIfError(false));

    }else{
    if(response.shippingLabelExtendedInfo!==null && response.shippingLabelExtendedInfo.length>0){
      // addExistingRMAList(config, callback, dispatch);
      response.shippingLabelExtendedInfo.map((res:any)=>{
       if(res.trackingNumber!==""){
        upsTrakingNoArr.push(res.trackingNumber);
       }
       if(res.shipToAddress.warehouseCode!==""){
         warehouseCodeArr.push(res.shipToAddress.warehouseCode);
        }
     });
      selectedWarehouseCode=warehouseCodeArr[0];
     }

// Add Existing RMA
     returnsService
    .addItemsToRmaList(config.rmaNumber, config.items)
    .then(() => {

      dispatch(setReturnIsPending(false));
      dispatch(
        setReturnAlert({
          show: true,
          type: 'success',
          message: `Return No. ${config.rmaNumber} has been saved.`,
          icon: 'rpds-check-circle'
        })
      );
      dispatch(setPartsToReturn([]));
      dispatch(getEligibleItems({}));
      dispatch(getEligibleCoreItems({}));
      if (config.submissionType === SAVE_FOR_LATER) {
        config.items.forEach(item => {
          dispatch(
            analyticsActions.rmaReturns(
              item,
              'saveReturnsForLater',
              config.rmaNumber,
              config.type
            )
          );
        });
      }

      /* Submit RMA now */
      returnsService
      .submitRMAListWithShippingLabel(config,rmaNumber,selectedWarehouseCode,upsTrakingNoArr,'PREPAID_SHIPPING')
      .then(() => {
        // dispatch(setReturnIsPending(false));
        dispatch(getEligibleItems({}));
        dispatch(getEligibleCoreItems({}));
        config.shippingLabelResponse=response;
        callback();
        dispatch(
          setReturnError({
            hasError: false,
            message: 'Returns submitted Sucessfully.',
            status: 200
          })
        );
      })
      .catch(() => {
        dispatch(setCloseModalIfError(true));
        dispatch(setReturnIsPending(false));
        // callback();
        dispatch(
          setReturnError({
            hasError: true,
            message:
              'An error has occurred when submitting return. Please contact support team via email at FeedbackCA@reliableparts.com and include your Account Number, Invoice Number/RMA Number and a screen capture.',
            status: 500
          })
        );
        dispatch(setCloseModalIfError(false));
      });
        // End Submit RMA
     
    })
    .catch(() => {
      dispatch(setReturnIsPending(false));
      dispatch(
        setReturnError({
          hasError: true,
          message:
            'Please review your RMA as one or more of your items is no longer eligible for return. Please remove these items and save/submit again.'
        })
      );
      dispatch(
        setReturnAlert({
          show: true,
          type: 'error',
          message: 'An error has ocurred while data was being saved',
          icon: 'rpds-error'
        })
      );
      dispatch(setCloseModalIfError(true));
    });
  }
  });
};

const onSaveForLater = (config: IReturnOptionConfig, callback?: () => void) => (
  dispatch: any
) => {
  dispatch(setReturnIsPending(true));
  dispatch(setReturnError());
  config.submissionType = SAVE_FOR_LATER;
  if (config.type === ADD_EXISTING) {
    addExistingRMAList(config, callback, dispatch);
  } else {
    addNewRMAList(config, callback, dispatch);
  }
};

const onSubmitNow = (
  config: IReturnShippingConfig,
  callback?: (rmaNumber?: string) => void
) => (dispatch: any) => {
  dispatch(setReturnIsPending(true));
  dispatch(setReturnError());

  if (config.type === ADD_EXISTING) {
    addExistingRMAList(
      config,
      () => {
        submitRMAList(
          config,
          config.rmaNumber,
          () => {
            dispatch(
              setReturnAlert({
                show: true,
                type: 'success',
                message: `Return No. ${config.rmaNumber} has been submitted.`,
                icon: 'rpds-check-circle'
              })
            );
            config.items.forEach(item => {
              dispatch(analyticsActions.productReturns(item));
              dispatch(
                analyticsActions.rmaReturns(
                  item,
                  'submitReturnsNow',
                  config.rmaNumber,
                  config.type
                )
              );
            });
            callback(config.rmaNumber);
          },
          dispatch
        );
      },
      dispatch
    );
  } else {
    addNewRMAList(
      config,
      () => {
        submitRMAList(
          config,
          config.rmaNumber,
          () => {
            dispatch(
              setReturnAlert({
                show: true,
                type: 'success',
                message: `Return No. ${config.rmaNumber} has been submitted.`,
                icon: 'rpds-check-circle'
              })
            );
            config.items.forEach(item => {
              dispatch(analyticsActions.productReturns(item));
              dispatch(
                analyticsActions.rmaReturns(
                  item,
                  'submitReturnsNow',
                  config.rmaNumber,
                  config.type
                )
              );
            });
            callback(config.rmaNumber);
          },
          dispatch
        );
      },
      dispatch
    );
  }
};

const onUpdateNow = (
  config: IReturnShippingConfig,
  callback?: (rmaNumber: string, resUpsCharges:any[]) => void
) => (dispatch: any) => {
  dispatch(setReturnIsPending(true));
  dispatch(setReturnError());
  if (config.type === ADD_EXISTING) {
        submitRMAListWithShippingLabelRequest(
          config,
          config.rmaNumber,
          'FROMEXISTING',
          () => {
            dispatch(
              setReturnAlert({
                show: true,
                type: 'success',
                message: `Return No. ${config.rmaNumber} has been submitted.`,
                icon: 'rpds-check-circle'
              })
            );
            config.items.forEach(item => {
              dispatch(analyticsActions.productReturns(item));
              dispatch(
                analyticsActions.rmaReturns(
                  item,
                  'submitReturnsNow',
                  config.rmaNumber,
                  config.type
                )
              );
            });
            callback(config.rmaNumber,config.shippingLabelResponse);
          },
          dispatch
        );
  } else {
    addNewRMAListForShiipingLabel(
      config,
      () => {
        submitRMAListWithShippingLabelRequest(
          config,
          config.rmaNumber,
          'FROMNEW',
          () => {
            dispatch(
              setReturnAlert({
                show: true,
                type: 'success',
                message: `Return No. ${config.rmaNumber} has been submitted.`,
                icon: 'rpds-check-circle'
              })
            );
            config.items.forEach(item => {
              dispatch(analyticsActions.productReturns(item));
              dispatch(
                analyticsActions.rmaReturns(
                  item,
                  'submitReturnsNow',
                  config.rmaNumber,
                  config.type
                )
              );
            });
            callback(config.rmaNumber,config.shippingLabelResponse);
          },
          dispatch
        );
      },
      dispatch
    );
  }
};

const shippingLabel = async (
  upsRequest: IReturnGenerateShippingLabel,
  callback: () => void,
  dispatch: any
) => {
  let rmaNumber = '';
  try {
    rmaNumber = await returnsService.generateShippingLabel(upsRequest.items);
    upsRequest.type=rmaNumber;
    callback();
    // upsRequest.type = rmaNumber;
  } catch (error) {
    dispatch(setReturnIsPending(false));
    dispatch(
      setReturnAlert({
        show: true,
        type: 'error',
        message:
          'Please review your RMA as one or more of your items is no longer eligible for return. Please remove these items and save/submit again.',
        icon: 'rpds-error'
      })
    );
  }
};


const onGenerateShippingLabel = (
  upsRequest: IReturnGenerateShippingLabel,
  callback?: (resUpsCharges?: any[]) => void
) => (dispatch: any) => {
  dispatch(setReturnIsPending(true));
  dispatch(setReturnError());
  returnsService.generateShippingLabel(upsRequest.items)
      .then((response: any) => {
        if (response.message !== null) {
            dispatch(setReturnIsPending(false));
            dispatch(getEligibleItems({}));
            dispatch(getEligibleCoreItems({}));
            dispatch(
              setReturnStatusAlert({
                show: true,
                message: response.message,
                type: 'error',
                icon: 'rpds-error'
              })
            );
            callback(response);
        }else{
        if (callback) {
          callback(response);
          // dispatch(setReturnIsPending(false));
        }
        }
      })
      .catch((e) => {
        /* dispatch(setReturnIsPending(false));
        dispatch(
        setReturnAlert({
        show: true,
        type: 'error',
        message:
          'UPS response error.',
        icon: 'rpds-error'
      })
    );
    */
      });
};

const downloadPdf = (rmaNumber: string, isPrepaidLabel: string) => (dispatch: any) => {
  dispatch(setReturnIsDownloadingPdf(true));
  dispatch(setReturnError());

  returnsService
    .generatePDF(rmaNumber,isPrepaidLabel)
    .then(pdf => {
      downloadFile(
        pdf,
        `${rmaNumber}-${localeFormatingInstance.getNowDate('short')}.pdf`
      );
      dispatch(setReturnIsDownloadingPdf(false));
    })
    .catch(() => {
      dispatch(
        setReturnError({
          hasError: true,
          message:
            'The PDF you are trying to download is not yet available. Please try again in a few minutes.'
        })
      );
      dispatch(setReturnIsDownloadingPdf(false));
    });
};

const updateItemsFromRmaList = (
  config: IReturnEditedOption,
  dispatch: any,
  callback: () => void
) => {
  const promises: any[] = [];

  config.items.forEach(item => {
    promises.push(
      returnsService.updateItemFromRmaList(config.rmaNumber, {
        id: item.id,
        reason: item.reason,
        returnQuantity: item.returnQuantity
      })
    );
  });

  // Promise.all(promises).then(() => {
  //   dispatch(setReturnIsPending(false));
  //   callback();
  // });
  Promise.all(promises)
    .then(() => {
      dispatch(setReturnIsPending(false));
      callback();
    })
    .catch(error => {
      dispatch(setReturnIsPending(false));

      dispatch(
        setReturnError({
          hasError: true,
          message: error.response.data
        })
      );
    });
};

const onSubmitAfterEditRmaList = (
  config: IReturnSubmittedOption,
  callback?: () => void
) => (dispatch: any) => {
  dispatch(setReturnIsPending(true));
  dispatch(setReturnError());

  if (config.deletedItems.length > 0) {
    const promises: any[] = [];

    config.deletedItems.forEach(itemId => {
      promises.push(
        returnsService.deleteItemFromRmaList(config.rmaNumber, itemId)
      );
    });

    Promise.all(promises).then(() => {
      returnsService
        .updateRmaList(config.rmaNumber, config.poNumber)
        .then(() => {
          submitRMAList(
            config,
            config.rmaNumber,
            () => {
              dispatch(
                setReturnStatusAlert({
                  show: true,
                  message: `Return No. ${config.rmaNumber} has been submitted.`,
                  type: 'success',
                  icon: 'rpds-check-circle'
                })
              );
              callback();
            },
            dispatch
          );
        });
    });
  } else {
    returnsService.updateRmaList(config.rmaNumber, config.poNumber).then(() => {
      submitRMAList(
        config,
        config.rmaNumber,
        () => {
          dispatch(
            setReturnStatusAlert({
              show: true,
              message: `Return No. ${config.rmaNumber} has been submitted.`,
              type: 'success',
              icon: 'rpds-check-circle'
            })
          );
          callback();
        },
        dispatch
      );
    });
  }
};

const onSaveAfterEditRmaList = (
  config: IReturnEditedOption,
  callback?: () => void
) => (dispatch: any) => {
  dispatch(setReturnIsPending(true));
  dispatch(setReturnError());

  if (config.deletedItems.length > 0) {
    const promises: any[] = [];

    config.deletedItems.forEach(itemId => {
      promises.push(
        returnsService.deleteItemFromRmaList(config.rmaNumber, itemId)
      );
    });

    Promise.all(promises).then(() => {
      returnsService
        .updateRmaList(config.rmaNumber, config.poNumber)
        .then(() => {
          updateItemsFromRmaList(config, dispatch, () => {
            dispatch(getEligibleItems({}));
            dispatch(getEligibleCoreItems({}));
            dispatch(
              setReturnStatusAlert({
                show: true,
                message: `Return No. ${config.rmaNumber} has been updated.`,
                type: 'success',
                icon: 'rpds-check-circle'
              })
            );
            callback();
          });
        });
    });
  } else {
    returnsService.updateRmaList(config.rmaNumber, config.poNumber).then(() => {
      updateItemsFromRmaList(config, dispatch, () => {
        dispatch(getEligibleItems({}));
        dispatch(getEligibleCoreItems({}));
        dispatch(
          setReturnStatusAlert({
            show: true,
            message: `Return No. ${config.rmaNumber} has been updated.`,
            type: 'success',
            icon: 'rpds-check-circle'
          })
        );
        callback();
      });
    });
  }
};

const onDeleteRMAList = (rmaNumber: string, callback: () => void) => (
  dispatch: any
) => {
  dispatch(setReturnIsPending(true));
  dispatch(setReturnError());

  returnsService
    .deleteRMAList(rmaNumber)
    .then(() => {
      dispatch(setReturnIsPending(false));
      dispatch(getEligibleItems({}));
      dispatch(getEligibleCoreItems({}));
      dispatch(
        setReturnStatusAlert({
          type: 'success',
          message: `Return No. ${rmaNumber} has been deleted.`,
          show: true,
          icon: 'rpds-trash'
        })
      );
      callback();
    })
    .catch(() => {
      dispatch(setReturnIsPending(false));
      dispatch(
        setReturnStatusAlert({
          type: 'error',
          message:
            'An error has occurred when submitting return. Please contact support team via email at FeedbackCA@reliableparts.com and include your Account Number, Invoice Number/RMA Number and a screen capture.',
          show: true,
          icon: 'rpds-error'
        })
      );
      callback();
    });
};

const setPartsToReturn = (partsToReturn: IEligibleItem[]) => {
  return {
    type: types.SET_RETURN_PARTS_TO,
    payload: partsToReturn
  };
};

const setEligibleData = (eligibleData: IEligibleResponse) => {
  return {
    type: types.SET_RETURN_ELIGIBLE_DATA,
    payload: eligibleData
  };
};

const setEligibleCoreData = (eligibleCoreData: IEligibleResponse) => {
  return {
    type: types.SET_RETURN_ELIGIBLE_CORE_DATA,
    payload: eligibleCoreData
  };
};

const setStatusData = (statusData: IStatusResponse) => {
  return {
    type: types.SET_RETURN_STATUS_DATA,
    payload: statusData
  };
};

const setReturnIsPending = (isPending: boolean) => {
  return {
    type: types.SET_RETURN_IS_PENDING,
    payload: isPending
  };
};

const setReturnIsDownloadingPdf = (isDownloadingPdf: boolean) => {
  return {
    type: types.SET_RETURN_IS_DOWNLOADING_PDF,
    payload: isDownloadingPdf
  };
};

const setReturnError = (error?: IReturnError) => {
  return {
    type: types.SET_RETURN_ERROR,
    payload: error
      ? error
      : {
          hasError: false,
          message: ''
        }
  };
};

const setRmaList = (rmaList: string[]) => {
  return {
    type: types.SET_RETURN_RMA_LIST,
    payload: rmaList
  };
};

const setReturnAlert = (returnAlert: IReturnAlert) => {
  return {
    type: types.SET_RETURN_ALERT,
    payload: returnAlert
  };
};

const setReturnStatusAlert = (returnAlert: IReturnAlert) => {
  return {
    type: types.SET_RETURN_STATUS_ALERT,
    payload: returnAlert
  };
};

const setCloseModalIfError = (isCloseModalIfError: boolean) => {
  return {
    type: types.SET_MODAL_IF_RETURN_ERROR,
    payload: isCloseModalIfError
  };
};

export default {
  getEligibleItems,
  getEligibleCoreItems,
  getStatusItems,
  addPartToReturnList,
  removePartFromReturnList,
  editPartFromReturnList,
  setPartsToReturn,
  onSaveForLater,
  onSubmitNow,
  onUpdateNow,
  setReturnAlert,
  setReturnStatusAlert,
  downloadPdf,
  onSubmitAfterEditRmaList,
  onDeleteRMAList,
  onSaveAfterEditRmaList,
  getRMALists,
  setReturnError,
  onGenerateShippingLabel
};
